import { Directive, ElementRef, Input, OnDestroy, Renderer2, TemplateRef, effect, inject } from '@angular/core';
import { RolesService } from '../../core/auth/roles.service';
import { Tooltip } from 'primeng/tooltip';

@Directive({
  selector: '[appFeatureAccess]',
  standalone: true,
  hostDirectives: [Tooltip],
})
export class FeatureAccessDirective implements OnDestroy {
  @Input({ required: true, alias: 'appFeatureAccess' }) requiredRole!: string;
  @Input() featureName: string = 'this feature';

  private rolesService = inject(RolesService);
  private el = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);
  private tooltip = inject(Tooltip);
  private clickUnlistener?: () => void;
  private keydownUnlistener?: () => void;

  // Original values captured just before first restriction; restored on unlock.
  private _origTabindex: string | null = null;
  private _origCursor: string = '';
  private _origTooltipContent: string | TemplateRef<HTMLElement> | undefined = undefined;
  private _origTooltipPosition: string | undefined = undefined;
  // Tracks whether restrictions are currently applied to avoid re-capturing originals
  // on subsequent re-evaluations while already restricted (e.g. free→standard when requiredRole='pro').
  private _isRestricted = false;

  constructor() {
    // Re-evaluates whenever the user's account status signal changes (e.g. post-upgrade)
    effect(() => {
      const hasAccess = this.rolesService.hasOrExceeds(this.requiredRole);
      this.applyAccessState(!hasAccess);
    });
  }

  private applyAccessState(restricted: boolean): void {
    this.clickUnlistener?.();
    this.keydownUnlistener?.();
    this.clickUnlistener = undefined;
    this.keydownUnlistener = undefined;

    const nativeElement = this.el.nativeElement;

    if (restricted) {
      // Guard against the first effect() run in the constructor, before Angular has bound
      // requiredRole. hasOrExceeds(undefined) returns false so restricted=true, but
      // requiredRole is not yet a valid string — skip until the input is available.
      if (!this.requiredRole) return;

      // Only capture original values on the FIRST transition into restricted state.
      // Re-capturing while already restricted would overwrite the true originals with
      // the restricted values (e.g. tabindex=-1, cursor=not-allowed), causing incorrect
      // restoration when the user eventually gains access.
      if (!this._isRestricted) {
        this._origTabindex = nativeElement.getAttribute('tabindex');
        this._origCursor = nativeElement.style.cursor;
        this._origTooltipContent = this.tooltip.content;
        this._origTooltipPosition = this.tooltip.tooltipPosition;
      }
      this._isRestricted = true;

      // Use aria-disabled instead of native disabled so that mouse events (hover) still fire,
      // allowing the PrimeNG Tooltip to trigger on restricted elements.
      this.renderer.setAttribute(nativeElement, 'aria-disabled', 'true');
      this.renderer.setStyle(nativeElement, 'cursor', 'not-allowed');

      // Remove from tab order for typical interactive elements
      const tagName = nativeElement.tagName?.toUpperCase();
      const isTypicallyInteractive =
        tagName === 'BUTTON' ||
        tagName === 'A' ||
        tagName === 'INPUT' ||
        tagName === 'SELECT' ||
        tagName === 'TEXTAREA';
      if (isTypicallyInteractive || nativeElement.hasAttribute('tabindex')) {
        this.renderer.setAttribute(nativeElement, 'tabindex', '-1');
      }

      // Block mouse and keyboard interaction in the *capture* phase so that events
      // are stopped before reaching child elements or earlier same-element listeners.
      const clickHandler = (event: Event): void => {
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      nativeElement.addEventListener('click', clickHandler, true);
      this.clickUnlistener = () => {
        nativeElement.removeEventListener('click', clickHandler, true);
      };

      const keydownHandler = (event: KeyboardEvent): void => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      nativeElement.addEventListener('keydown', keydownHandler, true);
      this.keydownUnlistener = () => {
        nativeElement.removeEventListener('keydown', keydownHandler, true);
      };

      // Wire tooltip via the hosted Tooltip directive instance (compile-time wiring)
      const roleLabel = this.requiredRole.charAt(0).toUpperCase() + this.requiredRole.slice(1);
      this.tooltip.content = `Upgrade to ${roleLabel} to use ${this.featureName}.`;
      this.tooltip.tooltipPosition = 'top';
    } else if (this._isRestricted) {
      // Only restore/clean up when actually transitioning from restricted → unrestricted.
      // Skipping this when _isRestricted=false prevents clobbering pre-existing tabindex,
      // cursor, or tooltip state on elements that were always accessible.
      this._isRestricted = false;
      this.renderer.removeAttribute(nativeElement, 'aria-disabled');

      // Restore cursor to its original value rather than unconditionally removing it.
      if (this._origCursor) {
        this.renderer.setStyle(nativeElement, 'cursor', this._origCursor);
      } else {
        this.renderer.removeStyle(nativeElement, 'cursor');
      }

      // Restore tabindex to its original value rather than unconditionally removing it.
      if (this._origTabindex !== null) {
        this.renderer.setAttribute(nativeElement, 'tabindex', this._origTabindex);
      } else {
        this.renderer.removeAttribute(nativeElement, 'tabindex');
      }

      // Restore tooltip state (including original content, in case the element had a tooltip).
      this.tooltip.content = this._origTooltipContent;
      this.tooltip.tooltipPosition = this._origTooltipPosition;
    }
  }

  ngOnDestroy(): void {
    this.clickUnlistener?.();
    this.keydownUnlistener?.();
  }
}
